import { HotelProviders } from '../constants';

// Free-text rate descriptions can be long; analytics only needs enough to group by.
const MAX_RATE_PLAN_TYPE_LENGTH = 100;

const CommissionSources = {
  SUPPLIER: 'supplier',
  // Tablet never returns an amount: the backend hard-codes 10% of the tax-exclusive
  // subtotal (TabletFormatHelpers.COMMISSION_PERCENTAGE), whether or not the rate is commissionable.
  DEFAULT_10: 'default10',
};

const round2 = (value) => (typeof value === 'number' ? Math.round(value * 100) / 100 : undefined);

const getCommissionSource = (priceProvider, agentCommission) => {
  if (typeof agentCommission !== 'number') return undefined;
  if (priceProvider === HotelProviders.TABLET) return CommissionSources.DEFAULT_10;
  return agentCommission > 0 ? CommissionSources.SUPPLIER : undefined;
};

// Measurement-only: describes the booked hotel rate so analytics can tell a
// non-commissionable rate from a commissionable one the supplier sent no amount for.
// Uses the first hotel itinerary with a selected product.
// Every field is undefined when unknown so analytics omits it instead of a fake value.
const computeHotelCommissionMeta = (trip) => {
  const itineraries = Array.isArray(trip?.hotelItineraries)
    ? trip.hotelItineraries
    : Object.values(trip?.hotelItineraries || {});

  const booked = itineraries
    .map((itinerary) => ({
      hotel: itinerary?.hotel,
      product: itinerary?.hotel?.products?.find(
        (product) => product.code === itinerary.selectedRoomCode,
      ),
    }))
    .find(({ product }) => product);

  if (!booked) return {};

  const { hotel, product } = booked;
  const roomPrice = product.roomPrice || {};
  const room = product.rooms?.[0] || {};
  const ratePlanType =
    typeof room.ratePlanType === 'string'
      ? room.ratePlanType.slice(0, MAX_RATE_PLAN_TYPE_LENGTH)
      : undefined;
  const commissionPercent = round2(roomPrice.agentCommissionPercentage);

  return {
    hotelIsCommissionable:
      typeof roomPrice.isCommissionable === 'boolean' ? roomPrice.isCommissionable : undefined,
    hotelCommissionSource: getCommissionSource(hotel?.priceProvider, roomPrice.agentCommission),
    hotelCommissionPercent: commissionPercent > 0 ? commissionPercent : undefined,
    hotelRatePlanCode: room.ratePlanCode || undefined,
    hotelRatePlanClientId: room.ratePlanClientId || undefined,
    hotelRatePlanTypeId: typeof room.ratePlanTypeId === 'number' ? room.ratePlanTypeId : undefined,
    hotelRatePlanType: ratePlanType || undefined,
    hotelIsRequestedRatePlanCode:
      typeof product.isMatchWithRequestedRatePlanCode === 'boolean'
        ? product.isMatchWithRequestedRatePlanCode
        : undefined,
    hotelPriceProvider: hotel?.priceProvider || undefined,
  };
};

export default computeHotelCommissionMeta;
